import axios from 'axios'
import { API_ENDPOINTS, API_BASE_URL } from '../utils/config.jsx'

// Create axios instance with default config
const api = axios.create({
  timeout: 10000,
  baseURL: API_BASE_URL
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const isLoginRequest = typeof error.config?.url === 'string' && error.config.url.includes('/api/auth/login')
    const hadToken = !!localStorage.getItem('admin_token')
    if (status === 401) {
      localStorage.removeItem('admin_token')
      // Avoid hard reload on login page; only redirect if user was previously authed
      if (hadToken && !isLoginRequest && window.location.pathname !== '/login') {
        window.location.assign('/login')
      }
    }
    return Promise.reject(error)
  }
)

// Hunt API methods
export const huntAPI = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.hunts)
    return response.data
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.hunts}/${id}`)
    return response.data
  },

  create: async (huntData) => {
    const response = await api.post(API_ENDPOINTS.hunts, huntData)
    return response.data
  },

  update: async (id, huntData) => {
    const response = await api.put(`${API_ENDPOINTS.hunts}/${id}`, huntData)
    return response.data
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.hunts}/${id}`)
    return response.data
  },

  getStats: async (id) => {
    const response = await api.get(API_ENDPOINTS.huntStats(id))
    return response.data
  },


}

// Session API methods
export const sessionAPI = {
  getAll: async (status = null) => {
    const params = status ? { status } : {}
    const response = await api.get(API_ENDPOINTS.sessions, { params })
    return response.data
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.sessions}/${id}`)
    return response.data
  },

  create: async (sessionData) => {
    const response = await api.post(API_ENDPOINTS.sessions, sessionData)
    return response.data
  },

  createManual: async (sessionData) => {
    const response = await api.post(API_ENDPOINTS.manualSession, sessionData)
    return response.data
  },

  expire: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.sessions}/${id}`)
    return response.data
  },

  deletePermanently: async (id) => {
    const response = await api.delete(API_ENDPOINTS.deleteSession(id))
    return response.data
  },

  // Enhanced progress tracking methods
  getProgressDetails: async (sessionId) => {
    const response = await api.get(`/api/sessions/${sessionId}/progress-summary`)
    return response.data
  },

  getHuntProgress: async (huntName) => {
    const response = await api.get(`/api/progress-admin/hunt/${encodeURIComponent(huntName)}`)
    return response.data
  },

  getProgressOverview: async () => {
    const response = await api.get('/api/progress-admin/overview')
    return response.data
  },

  refund: async (id, reason) => {
    const response = await api.post(`${API_ENDPOINTS.sessions}/${id}/refund`, { reason })
    return response.data
  },

  getStats: async () => {
    const response = await api.get(API_ENDPOINTS.sessionStats)
    return response.data
  },

  cleanup: async () => {
    const response = await api.post(`${API_ENDPOINTS.sessions}/cleanup`)
    return response.data
  }
}

// Webhook API methods
export const webhookAPI = {
  getHealth: async () => {
    const response = await api.get(API_ENDPOINTS.webhookHealth)
    return response.data
  },

  getStats: async () => {
    const response = await api.get(API_ENDPOINTS.webhookStats)
    return response.data
  },

  testWebhook: async (webhookData) => {
    
  }
}

// GDPR API methods
export const gdprAPI = {
  runNow: async (type = 'daily') => {
    const response = await api.post('/api/gdpr/scheduler/run-now', { type })
    return response.data
  },
  getLogs: async (params = {}) => {
    const response = await api.get('/api/gdpr/cleanup-logs', { params })
    return response.data
  }
}

// Analytics API
export const analyticsAPI = {
  // Get overview analytics
  getOverview: async (days = 30) => {
    try {
      const response = await api.get(API_ENDPOINTS.analyticsOverview(days));
      return response.data;
    } catch (error) {
      console.error('Analytics overview error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get hunt-specific analytics with currency conversion
  getHuntAnalytics: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.analyticsHunts);
      return response.data;
    } catch (error) {
      console.error('Hunt analytics error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get revenue analytics with commission calculations
  getRevenueAnalytics: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.analyticsRevenue());
      return response.data;
    } catch (error) {
      console.error('Revenue analytics error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get exchange rates
  getExchangeRates: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.analyticsExchangeRates);
      return response.data;
    } catch (error) {
      console.error('Exchange rates error:', error);
      return { success: false, error: error.message };
    }
  }
};

// Settings API methods
export const settingsAPI = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.settings)
    return response.data
  },

  getPricing: async () => {
    const response = await api.get(API_ENDPOINTS.settingsPricing)
    return response.data
  },

  updatePricing: async (price) => {
    const response = await api.put(API_ENDPOINTS.settingsPricing, { price_per_session: price })
    return response.data
  },

  getPriceHistory: async () => {
    const response = await api.get(API_ENDPOINTS.settingsPriceHistory)
    return response.data
  },

  updateSetting: async (key, value) => {
    const response = await api.put(`${API_ENDPOINTS.settings}/${key}`, { value })
    return response.data
  },

  getEmailSettings: async () => {
    const response = await api.get(API_ENDPOINTS.settingsEmail)
    return response.data
  },

  updateEmailSettings: async (emailSettings) => {
    const response = await api.put(API_ENDPOINTS.settingsEmail, emailSettings)
    return response.data
  },

  sendTestEmail: async (emailSettings) => {
    const response = await api.post(API_ENDPOINTS.settingsEmailTest, emailSettings)
    return response.data
  },

  getCompletionEmailSettings: async () => {
    const response = await api.get(`${API_ENDPOINTS.settings}/completion-email`)
    return response.data
  },

  updateCompletionEmailSettings: async (settings) => {
    const response = await api.put(`${API_ENDPOINTS.settings}/completion-email`, settings)
    return response.data
  },

  testCompletionEmail: async (testData) => {
    const response = await api.post(`${API_ENDPOINTS.settings}/completion-email/test`, testData)
    return response.data
  }
}

// New: test saved template email
export const settingsTemplateAPI = {
  sendTemplateTest: async (payload = {}) => {
    const response = await api.post(API_ENDPOINTS.settingsEmailTemplateTest, payload)
    return response.data
  }
}

// Auth API methods
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post(API_ENDPOINTS.login, { email, password })
    return response.data
  },
  listAdmins: async () => {
    const response = await api.get(API_ENDPOINTS.admins)
    return response.data
  },
  createAdmin: async (email, password, is_superadmin = false) => {
    const response = await api.post(API_ENDPOINTS.admins, { email, password, is_superadmin })
    return response.data
  },
  deleteAdmin: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.admins}/${id}`)
    return response.data
  }
}

// Health check
export const healthAPI = {
  check: async () => {
    const response = await api.get(API_ENDPOINTS.health)
    return response.data
  }
}

// Booking APIs
export const bookingAPI = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.bookingAPIs())
    return response.data
  },
  
  create: async (data) => {
    const response = await api.post(API_ENDPOINTS.bookingAPIs(), data)
    return response.data
  },
  
  update: async (id, data) => {
    const response = await api.put(`${API_ENDPOINTS.bookingAPIs()}/${id}`, data)
    return response.data
  },
  
  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.bookingAPIs()}/${id}`)
    return response.data
  },
  
  toggleActive: async (id) => {
    const response = await api.post(`${API_ENDPOINTS.bookingAPIs()}/${id}/toggle`)
    return response.data
  }
}

export default api
